import { memo, useRef, useState } from "react";

import mainClasses from "../styles.module.scss";
import { FormInputBox, FormInputIcon } from "../formControl";

import localClasses from "./styles.module.scss";

import type { IMultiselectProps } from ".";

import { useReadOnly } from "@/hooks";
import { Typography } from "@/components/atoms/typography";
import type { ID } from "@/constants";
import type { IOption } from "@/interfaces";
import { Icon } from "@/components/atoms/icons";
import {
  InputMenuItemPopover,
  InputPopover,
  InputPopoverContent,
  useInputPopoverContext,
} from "@/components/organisms/inputPopover";

const minWidthNum = 12;
const minWidth = `${minWidthNum}px`;

export const Multiselect = memo(function Multiselect(props: IMultiselectProps) {
  const { disabled: propsDisabled, readOnly: propsReadOnly, ...rest } = props;
  const readOnly = useReadOnly(propsReadOnly);
  const disabled = propsDisabled || rest.isLoading;

  return (
    <InputPopover ariaRole={"listbox"} disabled={disabled || readOnly}>
      <MultiselectInput {...rest} disabled={disabled} readOnly={readOnly} />
    </InputPopover>
  );
});

function MultiselectInput(props: IMultiselectProps) {
  const {
    readOnly,
    value = [],
    onChange,
    options,
    className,
    onFocus,
    onMouseDown,
    onBlur,
    disabled,
    isLoading,
    isError,
    placeholder,
    validation,
    ...rest
  } = props;

  const { refs, getReferenceProps, setOpen, activeIndex } =
    useInputPopoverContext();

  const inputRef = useRef<HTMLInputElement>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const hiddenSpanRef = useRef<HTMLSpanElement>(null);
  const [inputValue, setInputValue] = useState("");

  const items = options?.filter(
    (item) => item?.value?.toLowerCase()?.includes(inputValue?.toLowerCase()),
  );

  // is there any selected value or input is empty
  const hasSelectedValue = value.length > 0;

  // can user clear or modify the value or not
  const canClearItem =
    hasSelectedValue && !readOnly && !disabled && !isLoading && !isError;

  const canClearAllItem = canClearItem && inputValue.length === 0;

  const isSelected = (key: ID) => value.some((item) => item.key === key);

  const focusOnInput = () => inputRef.current?.focus();

  function removeValue(key: ID) {
    const values = value.filter((item) => item.key !== key);

    onChange?.(values);
  }

  function addValue(item: IOption<ID>) {
    onChange?.([...value, item]);
  }

  function removeLastItem() {
    if (value.length > 0 && value !== undefined) {
      const values = [...value];

      values.pop();
      onChange?.(values);
    }
  }

  function handleItemSelection() {
    if (activeIndex !== null) {
      const activeItem = items[activeIndex];
      const index = value.findIndex((item) => item.key === activeItem.key);

      if (index === -1) addValue(activeItem);
      else removeValue(activeItem.key);
      // clear the input
      setInputValue("");
    }
  }

  const getInputWidth = () => {
    if (!hasSelectedValue) return "100%";
    if (inputValue === "") return minWidth;
    if (hiddenSpanRef.current && inputContainerRef.current) {
      return `${Math.min(
        inputContainerRef.current.clientWidth,
        hiddenSpanRef.current.clientWidth + minWidthNum,
      )}px`;
    }
    return minWidth;
  };

  return (
    <>
      <FormInputBox
        ref={refs.setReference}
        disabled={!!disabled}
        readOnly={!!readOnly}
        validation={validation}
        onMouseDown={(event) => {
          event.preventDefault();
          focusOnInput();
        }}
      >
        <div className={mainClasses.inputContainer}>
          <div className={localClasses.chipOutline} ref={inputContainerRef}>
            {value.map((item) => (
              <div key={item.key} className={mainClasses.chip}>
                <Typography createSpan>{item.value}</Typography>
                {canClearItem && (
                  <div className={"flex-item-center cursor-pointer"}>
                    <span
                      className={mainClasses.chipRemoveIcon}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      onClick={() => removeValue(item.key)}
                    >
                      &#x2715;
                    </span>
                  </div>
                )}
              </div>
            ))}
            <span ref={hiddenSpanRef} className={localClasses.hiddenSpan}>
              {inputValue}
            </span>
            <input
              {...getReferenceProps({
                style: { minWidth, width: getInputWidth() },
                "aria-autocomplete": "list",
                onKeyDown(event) {
                  switch (event.key) {
                    case "Enter":
                      // prevent form submitting
                      event.preventDefault();
                      handleItemSelection();
                      break;

                    case "Tab":
                      setOpen(false);
                      break;

                    case "Backspace":
                      if (inputValue === "") removeLastItem();
                      break;

                    default:
                      if (event.key.length === 1 && !open) setOpen(true);
                      break;
                  }
                },
              })}
              className={mainClasses.input}
              readOnly={readOnly}
              disabled={disabled}
              ref={inputRef}
              value={inputValue}
              autoComplete={"off"}
              placeholder={hasSelectedValue ? undefined : placeholder}
              onChange={(event) =>
                setInputValue(event.target.value.trimStart())
              }
              onFocus={(event) => {
                setOpen(true);
                onFocus?.(event);
              }}
              onBlur={(event) => {
                setInputValue("");
                // setOpen(false);
                onBlur?.(event);
              }}
              onMouseDown={(event) => {
                event.stopPropagation();
                onMouseDown?.(event);
              }}
              {...rest}
            />
          </div>
        </div>
        <FormInputIcon
          iconName={canClearAllItem ? "CloseCircle" : "Magnifer"}
          type={canClearAllItem ? "actionButton" : "bgIcon"}
          onMouseDown={(event) => {
            if (canClearAllItem) {
              // prevent to focus out input
              event.preventDefault();
              // prevent to call `FormControlBox` onMouseDownEvent
              event.stopPropagation();
            }
          }}
          onClick={() => {
            // clear the value
            if (canClearAllItem) onChange?.([]);
          }}
          {...{ isLoading, isError, disabled, readOnly }}
        />
      </FormInputBox>
      <InputPopoverContent>
        {items.map((item, index) => {
          const selected = isSelected(item.key);

          return (
            <InputMenuItemPopover
              key={item.key}
              index={index}
              onClick={handleItemSelection}
              onMouseDown={
                // cause we don't want to lose input's focus on icon clicking event
                (event) => event.preventDefault()
              }
              selected={selected}
            >
              <div className={localClasses.menuItem}>
                <Typography createSpan>{item.value}</Typography>
                {selected && (
                  <Icon
                    // TODO: replace with Tick
                    name={"CheckRead"}
                    className={"color-primary-main"}
                  />
                )}
              </div>
            </InputMenuItemPopover>
          );
        })}
      </InputPopoverContent>
    </>
  );
}
